import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from 'react-router-dom';
import LoadingBar from "react-top-loading-bar";
import { mutate } from "swr";
import { AssetSvg } from "../../assets";
import { AppRouteUi, PaginationSettings } from "../../configs";
import { useAppSelector, VRIAppStateType } from "../../core";
import { AdminPageTemplate, ColumnToolTip, ModalConfirmDelete, ModalCreateVendor, ModalEditVendorPhone, ModalEditVendorOverflowValue, ModalEditVendorOverflowType } from "../../features";
import { ApiSchemaVendorData, VendorConnectionType } from "../../models";
import { useFunctionalityVendors } from "../hooks";
import { useFunctionalityPagedVendors } from "../hooks/functionalityVendors";
import { formatUSPhoneNumber } from "../../utils/phoneNumberFormatting";

type Props = {
  parentCompanyId: string;
};
export function PageVendors(props: Props): JSX.Element {
  const [loaderProgress, setLoaderProgress] = useState(0);
  const { parentCompanyId } = props;
  const { profile } = useAppSelector((state: VRIAppStateType) => state.auth);
  const [createModalShow, setCreateModalShow] = useState(false);
  const [editOverflowValueModalShow, setEditOverflowValueModalShow] = useState(false);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [pageIndex, setPageIndex] = useState(PaginationSettings.initialPageIndex);
  const [pageSize, setPageSize] = useState(PaginationSettings.initialPageSize);
  const [searchText, setSearchText] = useState<string>("");
  const [dataToDelete, setDataToDelete] = useState<string>("");
  const [hasPagingData, setHasPagingData] = useState<boolean>(false);
  const [vendorName, setVendorName] = useState<string | null>(null);

  const [selectedVendor, setSelectedVendor] =
    useState<ApiSchemaVendorData>(Object);

  const { onDeleteVendor } = useFunctionalityVendors({
    parentCompanyId: parentCompanyId ? parentCompanyId : profile?.companyId,
  });
  const { pagedVendorList, pagedVendorListCount, pagedVendorUrl } =
    useFunctionalityPagedVendors({
      parentCompanyId: parentCompanyId ? parentCompanyId : profile?.companyId,
      pageSize,
      pageNo: pageIndex,
      searchText: searchText?.trimStart(),
      setLoaderProgress,
    });

  useEffect(() => {
    if (pagedVendorList?.length > 0) {
      setHasPagingData(true);
    } else {
      setHasPagingData(false);
    }
  }, [pagedVendorList]);

  const handleSearch = (e: any) => {
    ReactDOM.unstable_batchedUpdates(() => {
      setSearchText(e.target.value);
      setPageIndex(1);
    });
  };
  const onChangePageIndex = (index: number) => {
    setPageIndex(index);
  };
  const onChangePageSize = (size: number) => {
    setPageSize(size);
  };

  const onDeleteHandler = () => {
    onDeleteVendor(dataToDelete)
      .then(() => {
        setDeleteModalShow(false);
        mutate(pagedVendorUrl);
      })
      .catch((e) => console.error(e));
  };

  const vendorsTableColumns = [
    {
      name: <ColumnToolTip data="Name" />,
      selector: (row: ApiSchemaVendorData) => {
        return <ColumnToolTip data={row.name} />;
      },
    },
    {
      name: "Overflow Type",
      selector: (row: ApiSchemaVendorData) => (
        <>
          <span className="mr-3">
            <ColumnToolTip data={VendorConnectionType[row.vendorOverflowType]} />
          </span>
        </>
      ),
    },
    {
      name: "Overflow Value",
      selector: (row: ApiSchemaVendorData) => (
        <div className="d-flex align-items-center gap-4" style={{ minWidth: 0 }}>
          <div
            className="flex-grow-1 text-truncate mr-2"
            style={{ minWidth: 0 }}            
            title={row.vendorOverflowValue}         
          >
            <ColumnToolTip data={row.vendorOverflowType === 1 ? formatUSPhoneNumber(row.vendorOverflowValue) : (row.vendorOverflowValue)} />
          </div>

          <button
            className="btn btn-link svg-color px-0 flex-shrink-0"
            onClick={() => {
              setVendorName(row.name ?? null);
              setSelectedVendor(row);
              setEditOverflowValueModalShow(true);
            }}
            aria-label="Edit overflow value"
          >
            <span><AssetSvg.Edit /></span>
          </button>
        </div>
      ),
    },

    {
      name: "Actions",
      allowOverflow: true,
      selector: (row: ApiSchemaVendorData) => {
        return (
          <>
            <Link
              to={{
                pathname: AppRouteUi.Vendors.Profile.Load(row.id),
                state: {
                  companyId: row.parentCompanyId,
                  vendorName: row.name,
                  vendorType: row.vendorOverflowType
                },
              }}
              className="btn btn-link svg-color  px-0"
            >
              <span >
                <AssetSvg.ViewProfile />
              </span>
            </Link>
            <button
              className="btn btn-link svg-color py-0 "
              onClick={() => {
                setVendorName(row.name ?? null)
                setDeleteModalShow?.(true);
                setDataToDelete(row.id);
              }}
            >
              <span >
                <AssetSvg.Delete />
              </span>
            </button>
          </>
        );
      },
    },
  ];

  return (
    <>
      <LoadingBar
        progress={loaderProgress}
        onLoaderFinished={() => setLoaderProgress(0)}
      />
      <AdminPageTemplate
        pagingKey="PageVendors"
        loaderProgress={loaderProgress}
        pageTitle="Vendor"
        modalControlBtnLabel="Create Vendor"
        tableHeaders={vendorsTableColumns}
        tableData={pagedVendorList || []}
        setCreateModalShow={setCreateModalShow}
        searchText={searchText}
        handleSearch={handleSearch}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        count={pagedVendorListCount}
        onChangePageIndex={onChangePageIndex}
        onChangePageSize={onChangePageSize}
        hasPagingData={hasPagingData}
        setHasPagingData={setHasPagingData}
      />
      {createModalShow ? (
        <ModalCreateVendor
          parentCompanyId={parentCompanyId}
          show={createModalShow}
          handleShow={setCreateModalShow}
          pagedVendorUrl={pagedVendorUrl}
        />
      ) : null}
      {/* {editPhoneModalShow ? (
        <ModalEditVendorPhone
          parentCompanyId={parentCompanyId}
          show={editPhoneModalShow}
          handleShow={setEditPhoneModalShow}
          selectedVendor={selectedVendor}
          pagedVendorUrl={pagedVendorUrl}
        />
      ) : null} */}
      {/* {editOverflowTypeModalShow ? (
        <ModalEditVendorOverflowType
          parentCompanyId={parentCompanyId}
          show={editOverflowTypeModalShow}
          handleShow={setEditOverflowTypeModalShow}
          selectedVendor={selectedVendor}
          pagedVendorUrl={pagedVendorUrl}
        />
      ) : null} */}
      {editOverflowValueModalShow ? (
        <ModalEditVendorOverflowValue
          parentCompanyId={parentCompanyId}
          show={editOverflowValueModalShow}
          handleShow={setEditOverflowValueModalShow}
          selectedVendor={selectedVendor}
          pagedVendorUrl={pagedVendorUrl}
        />
      ) : null}
      <ModalConfirmDelete
        show={deleteModalShow}
        handleShow={setDeleteModalShow}
        deleteHandler={onDeleteHandler}
        itemName={vendorName}
      />
    </>
  );
}
